import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  ValidationModule,
  RowSelectionModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  RowStyleModule,
} from "ag-grid-community";
import { PayByLinkListStyled, SkeletonWrapper } from "./styled";
import {
  CustomDropdown,
  CustomTableFooter,
  StatusBadge,
  SnackMsg,
  TableSkeletonLoader,
  ClearFilters,
  NoRowsOverlay,
} from "../../components";
import {
  COMMON_PAYMENT_CONSTANTS,
  PAY_BY_LINK_CONSTANTS,
  PRODUCT_HEADERS,
} from "../../config/constants";
import {
  COLUMN_DEFS,
  PAGE_ROUTE,
  SEARCH_CATEGORIES,
  STATUS_FILTER_OPTIONS,
  PAYMENT_STATUS_FILTER_OPTIONS,
} from "./constants";
import { colors } from "../../config/colors";
import { SearchLogo } from "assets/icons";
import { Skeleton } from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { ADMIN_TYPE, API_URL } from "utils/Constants";
import { ApiService } from "utils/ApiService";

const NoRowsOverlayComponent = () => (
  <NoRowsOverlay
    title={PAY_BY_LINK_CONSTANTS.NO_DATA_MESSAGE}
    subtitle={PAY_BY_LINK_CONSTANTS.NO_DATA_SUBTITLE}
  />
);

const PayByLinkList = () => {
  const navigate = useNavigate();

  const [snackMsg, setSnackMsg] = useState("");
  const [showSnack, setShowSnack] = useState(false);

  const showFlashMessage = useCallback((msg) => {
    setSnackMsg(msg);
    setShowSnack(true);
  }, []);

  // Read and immediately clear flash message set by cancel flow in details screen
  useEffect(() => {
    const msg = sessionStorage.getItem("flashMsg");
    if (msg) {
      sessionStorage.removeItem("flashMsg");
      showFlashMessage(msg);
    }
  }, [showFlashMessage]);

  const [checkouts, setCheckouts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Search — debounced
  const [searchText, setSearchText] = useState("");
  const [searchCategory, setSearchCategory] = useState(SEARCH_CATEGORIES[0]);
  const [debouncedText, setDebouncedText] = useState("");
  const [debouncedCategory, setDebouncedCategory] = useState(
    SEARCH_CATEGORIES[0],
  );

  // Instant filters — no debounce needed
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_OPTIONS[0]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(
    PAYMENT_STATUS_FILTER_OPTIONS[0],
  );
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const getMerchantId = useCallback(() => {
    const role = localStorage.getItem("role");
    if (role === ADMIN_TYPE.SUPERADMIN)
      return localStorage.getItem("globalMerchantId") || "";
    if (role === ADMIN_TYPE.SCHOOLADMIN)
      return localStorage.getItem("schoolAdminMerchantId") || "";
    return "";
  }, []);

  // Builds the search payload from current filter state
  const buildPayload = useCallback(
    (overrides = {}) => {
      const role = localStorage.getItem("role");
      const merchantId = getMerchantId();

      const payload = { pageNo, pageSize, ...overrides };

      if (role === ADMIN_TYPE.SUPERADMIN && merchantId) {
        payload.merchantId = merchantId;
      }
      if (debouncedText.trim()) {
        payload[debouncedCategory.value] = debouncedText.trim();
      }
      if (statusFilter.value) {
        payload.status = statusFilter.value;
      }
      if (paymentStatusFilter.value) {
        payload.paymentStatus = paymentStatusFilter.value;
      }
      if (dateFrom) {
        payload.createdAtFrom = dayjs(dateFrom).format("YYYY-MM-DDTHH:mm:ss");
      }
      if (dateTo) {
        payload.createdAtTo = dayjs(dateTo)
          .endOf("day")
          .format("YYYY-MM-DDTHH:mm:ss");
      }

      return payload;
    },
    [
      pageNo,
      pageSize,
      debouncedText,
      debouncedCategory,
      statusFilter,
      paymentStatusFilter,
      dateFrom,
      dateTo,
      getMerchantId,
    ],
  );

  // Debounce search text only
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedText(searchText);
      setDebouncedCategory(searchCategory);
      setPageNo(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText, searchCategory]);

  const fetchCheckouts = useCallback(async () => {
    setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await ApiService.postRequest(
        API_URL.MANAGE_CHECKOUT_SEARCH,
        payload,
        PRODUCT_HEADERS,
      );

      if (response?.status?.code === "Y") {
        setCheckouts(response.body?.items ?? []);
        setTotalCount(response.body?.totalElements ?? 0);
      } else {
        if (response?.status?.message) {
          setSnackMsg(response.status.message);
          setShowSnack(true);
        }
        setCheckouts([]);
        setTotalCount(0);
      }
    } catch (err) {
      setCheckouts([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [buildPayload]);

  useEffect(() => {
    fetchCheckouts();
  }, [fetchCheckouts]);

  // ── Filter handlers ──────────────────────────────────────────────────────

  const handleSearchCategoryChange = useCallback((selectedIndex) => {
    setSearchCategory(SEARCH_CATEGORIES[selectedIndex]);
    setSearchText("");
    setDebouncedText("");
  }, []);

  const handleStatusFilterChange = useCallback((selectedIndex) => {
    setStatusFilter(STATUS_FILTER_OPTIONS[selectedIndex]);
    setPageNo(0);
  }, []);

  const handlePaymentStatusFilterChange = useCallback((selectedIndex) => {
    setPaymentStatusFilter(PAYMENT_STATUS_FILTER_OPTIONS[selectedIndex]);
    setPageNo(0);
  }, []);

  const handleDateFromChange = useCallback((val) => {
    setDateFrom(val);
    setPageNo(0);
  }, []);

  const handleDateToChange = useCallback((val) => {
    setDateTo(val);
    setPageNo(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatusFilter(STATUS_FILTER_OPTIONS[0]);
    setPaymentStatusFilter(PAYMENT_STATUS_FILTER_OPTIONS[0]);
    setDateFrom(null);
    setDateTo(null);
    setSearchText("");
    setSearchCategory(SEARCH_CATEGORIES[0]);
    setDebouncedText("");
    setDebouncedCategory(SEARCH_CATEGORIES[0]);
    setPageNo(0);
  }, []);

  // ── Pagination handlers ──────────────────────────────────────────────────

  const onPageSizeChanged = useCallback((event) => {
    setPageSize(parseInt(event.target.value));
    setPageNo(0);
  }, []);

  const onLeftArrow = useCallback(() => {
    if (pageNo > 0) setPageNo((p) => p - 1);
  }, [pageNo]);

  const onRightArrow = useCallback(() => {
    if (pageNo < Math.ceil(totalCount / pageSize) - 1) setPageNo((p) => p + 1);
  }, [pageNo, totalCount, pageSize]);

  const columnDefs = useMemo(
    () =>
      COLUMN_DEFS.map((col) => {
        if (col.field === "status" || col.field === "paymentStatus") {
          return {
            ...col,
            cellRenderer: (params) => <StatusBadge status={params.value} />,
          };
        }
        return col;
      }),
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: false,
      sortable: false,
      filter: false,
      suppressMovable: true,
    }),
    [],
  );

  const onRowClicked = useCallback(
    (event) => {
      sessionStorage.setItem("selectedCheckout", JSON.stringify(event.data));
      navigate(`${PAGE_ROUTE.PAY_BY_LINK}/${event.data.ott}`);
    },
    [navigate],
  );

  const renderTableSkeleton = () => (
    <SkeletonWrapper>
      <Skeleton
        variant="rectangular"
        width="100%"
        height={42}
        sx={{ marginBottom: "10px", borderRadius: "4px" }}
      />
      <TableSkeletonLoader rowCount={pageSize} rowHeight={52} />
    </SkeletonWrapper>
  );

  return (
    <PayByLinkListStyled>
      {showSnack ? (
        <SnackMsg
          msg={snackMsg}
          show={showSnack}
          onClose={() => setShowSnack(false)}
        />
      ) : null}

      {/* Toolbar with filters */}
      <div className="toolbar">
        <div className="search-filters">
          {/* Link Status Filter */}
          <CustomDropdown
            label={PAY_BY_LINK_CONSTANTS.LINK_STATUS}
            value={statusFilter.label}
            items={STATUS_FILTER_OPTIONS.map((opt) => opt.label)}
            height={36}
            width={150}
            onChange={handleStatusFilterChange}
            disabled={isLoading}
          />

          {/* Payment Status Filter */}
          <CustomDropdown
            label={COMMON_PAYMENT_CONSTANTS.PAYMENT_STATUS}
            value={paymentStatusFilter.label}
            items={PAYMENT_STATUS_FILTER_OPTIONS.map((opt) => opt.label)}
            height={36}
            width={170}
            onChange={handlePaymentStatusFilterChange}
            disabled={isLoading}
          />

          {/* Date Range Section */}
          <div className="date-range-container">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="date-picker-wrapper">
                <DatePicker
                  label={PAY_BY_LINK_CONSTANTS.DATE_FROM}
                  value={dateFrom}
                  onChange={(val) => handleDateFromChange(val)}
                  format="DD/MM/YYYY"
                  disabled={isLoading}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: { size: "small", placeholder: "DD/MM/YYYY" },
                  }}
                />
              </div>
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="date-picker-wrapper">
                <DatePicker
                  label={PAY_BY_LINK_CONSTANTS.DATE_TO}
                  value={dateTo}
                  onChange={(val) => handleDateToChange(val)}
                  format="DD/MM/YYYY"
                  disabled={isLoading}
                  minDate={dateFrom}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: { size: "small", placeholder: "DD/MM/YYYY" },
                  }}
                />
              </div>
            </LocalizationProvider>
          </div>

          {/* Search Category Dropdown */}
          <CustomDropdown
            label={COMMON_PAYMENT_CONSTANTS.SEARCH_BY}
            value={searchCategory.label}
            items={SEARCH_CATEGORIES.map((cat) => cat.label)}
            height={36}
            width={160}
            onChange={handleSearchCategoryChange}
            disabled={isLoading}
          />

          {/* Search Input */}
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              disabled={isLoading || isInitialLoading}
              style={{
                cursor: isLoading || isInitialLoading ? "not-allowed" : "text",
                backgroundColor:
                  isLoading || isInitialLoading ? "#f9f9f9" : "white",
              }}
            />
            <SearchLogo className="search-icon" />
          </div>

          {/* Clear Filters Button */}
          <ClearFilters
            hasActiveFilters={
              statusFilter.value !== "" ||
              paymentStatusFilter.value !== "" ||
              dateFrom ||
              dateTo ||
              searchText
            }
            onClear={handleClearFilters}
            label={COMMON_PAYMENT_CONSTANTS.CLEAR_FILTERS}
          />
        </div>
      </div>

      <div className="ag-grid-container">
        <div className="ag-theme-material">
          {isLoading ? (
            renderTableSkeleton()
          ) : (
            <AgGridReact
              rowData={checkouts}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onRowClicked={onRowClicked}
              rowSelection="single"
              animateRows={true}
              suppressPaginationPanel={true}
              headerHeight={42}
              rowHeight={52}
              rowStyle={{
                borderTop: `1px solid ${colors.storkeLight}`,
                borderBottom: `1px solid ${colors.storkeLight}`,
              }}
              noRowsOverlayComponent={NoRowsOverlayComponent}
              modules={[
                ClientSideRowModelModule,
                ValidationModule,
                RowSelectionModule,
                TextFilterModule,
                NumberFilterModule,
                DateFilterModule,
                CustomFilterModule,
                RowStyleModule,
              ]}
            />
          )}
        </div>
      </div>

      {/* Custom Pagination Footer - Only show when not loading and has data */}
      {!isLoading && checkouts.length > 0 && (
        <CustomTableFooter
          pageNo={pageNo}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageSizeChanged={onPageSizeChanged}
          onLeftArrow={onLeftArrow}
          onRightArrow={onRightArrow}
        />
      )}
    </PayByLinkListStyled>
  );
};

export default PayByLinkList;
